import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  ButtonGroup,
  LinearProgress,
  Tab,
  Tabs,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  Warning,
  CheckCircle,
  Error,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  OpenInNew,
  Add,
  Block,
  Refresh,
  Download,
  Link,
  LinkOff,
  Analytics,
  AccountTree,
  Stop
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

// Форматирование адреса
const formatAddress = (address) => {
  if (!address) return '-';
  if (address.length <= 16) return address;
  return `${address.substring(0, 8)}...${address.slice(-6)}`;
};

// Форматирование числа
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  return parseFloat(num).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
};

const WalletScanner = () => {
  // Проверяем URL параметр ?profit=username для автозапуска
  const urlParams = new URLSearchParams(window.location.search);
  const autoScanUser = urlParams.get('profit');

  // Вкладки (0 = Обзор пула, 1 = Анализ кошелька, 2 = Profit Scanner)
  const [activeTab, setActiveTab] = useState(autoScanUser ? 2 : 0);

  // Фильтр типа операций в пуле
  const [poolTypeFilter, setPoolTypeFilter] = useState('all'); // 'all', 'buy', 'sell'

  // Фильтры скрытия MM и системного кошелька
  const [hideMM, setHideMM] = useState(false);
  const [hideSystem, setHideSystem] = useState(false);

  // Анализ одного кошелька
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [addFlipperReason, setAddFlipperReason] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedSection, setExpandedSection] = useState({
    blockchain: true,
    system: true,
    buys: false,
    sells: false
  });

  // Обзор пула
  const [poolLimit, setPoolLimit] = useState(30);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolDetailed, setPoolDetailed] = useState(false);
  const [poolData, setPoolData] = useState(null);
  const [poolError, setPoolError] = useState('');
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [connectionDetails, setConnectionDetails] = useState({});
  const [loadingConnection, setLoadingConnection] = useState({});

  // Добавление в флипперы из пула
  const [poolFlipperAddress, setPoolFlipperAddress] = useState(null);
  const [poolFlipperReason, setPoolFlipperReason] = useState('');
  const [showPoolFlipperDialog, setShowPoolFlipperDialog] = useState(false);

  // Глубокий анализ связей
  const [deepScanDepth, setDeepScanDepth] = useState(2);
  const [deepScanLoading, setDeepScanLoading] = useState(false);
  const [deepScanTaskId, setDeepScanTaskId] = useState(null);
  const [deepScanProgress, setDeepScanProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [deepScanResult, setDeepScanResult] = useState(null);
  const [deepScanError, setDeepScanError] = useState('');

  // Profit Scanner
  const [profitUsername, setProfitUsername] = useState(autoScanUser || '');
  const [profitLoading, setProfitLoading] = useState(false);
  const [profitData, setProfitData] = useState(null);
  const [profitError, setProfitError] = useState('');

  // Автозапуск скана если пришли по ссылке ?profit=username
  useEffect(() => {
    if (autoScanUser && !profitData && !profitLoading) {
      scanProfit(autoScanUser);
    }
  }, [autoScanUser]);

  // Profit Scanner - сканирование профита по username
  const scanProfit = async (overrideUsername) => {
    const username = (overrideUsername || profitUsername).trim().replace(/^@/, '');
    if (!username) {
      setProfitError('Введите username');
      return;
    }
    setProfitUsername(username);
    setProfitLoading(true);
    setProfitError('');
    setProfitData(null);

    try {
      const response = await adminAPI.post('/wallet-scanner/profit-scan', { username });
      if (response.data.success) {
        setProfitData(response.data.data);
      } else {
        setProfitError(response.data.error || 'Ошибка сканирования');
      }
    } catch (err) {
      setProfitError(err.response?.data?.error || err.message);
    }

    setProfitLoading(false);
  };

  // Отправить HTML отчёт в Telegram
  const [reportSending, setReportSending] = useState(false);
  const sendProfitReport = async () => {
    if (!profitData) return;
    setReportSending(true);
    try {
      await adminAPI.post('/wallet-scanner/profit-report', { profitData });
      setProfitError('');
      alert('Отчёт отправлен в Telegram!');
    } catch (err) {
      setProfitError('Ошибка отправки: ' + (err.response?.data?.error || err.message));
    }
    setReportSending(false);
  };

  // Анализ кошелька
  const analyzeWallet = async () => {
    if (!address || address.length < 6) {
      setError('Введите адрес кошелька (минимум 6 символов)');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await adminAPI.post('/wallet-scanner/analyze', { address });
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.error || 'Ошибка анализа');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }

    setLoading(false);
  };

  // Глубокий анализ связей
  const startDeepScan = async () => {
    if (!address || address.length < 6) {
      setDeepScanError('Сначала введите адрес кошелька');
      return;
    }

    setDeepScanLoading(true);
    setDeepScanError('');
    setDeepScanResult(null);
    setDeepScanProgress({ current: 0, total: 0, percent: 0 });

    try {
      const response = await adminAPI.startDeepConnectionScan(address, deepScanDepth, 20);
      if (response.data.success) {
        setDeepScanTaskId(response.data.taskId);
        // Начинаем polling статуса
        pollDeepScanStatus(response.data.taskId);
      } else {
        setDeepScanError(response.data.message || 'Ошибка запуска сканирования');
        setDeepScanLoading(false);
      }
    } catch (err) {
      setDeepScanError(err.response?.data?.message || err.message);
      setDeepScanLoading(false);
    }
  };

  const pollDeepScanStatus = async (taskId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await adminAPI.getDeepConnectionStatus(taskId);
        const { status, progress, result } = response.data;

        if (progress) {
          setDeepScanProgress(progress);
        }

        if (status === 'completed') {
          clearInterval(pollInterval);
          setDeepScanLoading(false);
          setDeepScanResult(result);
        } else if (status === 'error' || status === 'aborted') {
          clearInterval(pollInterval);
          setDeepScanLoading(false);
          setDeepScanError(result?.error || `Сканирование ${status === 'aborted' ? 'остановлено' : 'завершилось с ошибкой'}`);
        }
      } catch (err) {
        clearInterval(pollInterval);
        setDeepScanLoading(false);
        setDeepScanError('Ошибка получения статуса: ' + err.message);
      }
    }, 2000); // Опрашиваем каждые 2 секунды
  };

  const abortDeepScan = async () => {
    if (!deepScanTaskId) return;

    try {
      await adminAPI.abortDeepConnectionScan(deepScanTaskId);
      setDeepScanLoading(false);
      setDeepScanError('Сканирование остановлено пользователем');
    } catch (err) {
      console.error('Ошибка остановки сканирования:', err);
    }
  };

  // Добавить в флипперы (из анализа одного кошелька)
  const addToFlippers = async () => {
    if (!result?.address) return;

    try {
      const response = await adminAPI.post('/wallet-scanner/add-flipper', {
        address: result.address,
        reason: addFlipperReason || `Добавлен через сканер. Продал ${result.blockchain?.soldPercent}%`
      });
      if (response.data.success) {
        setShowAddDialog(false);
        setAddFlipperReason('');
        analyzeWallet();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // Добавить в флипперы из обзора пула
  const addPoolWalletToFlippers = async () => {
    if (!poolFlipperAddress) return;

    try {
      const response = await adminAPI.post('/wallet-scanner/add-flipper', {
        address: poolFlipperAddress.wallet || poolFlipperAddress.walletFriendly,
        reason: poolFlipperReason || `Добавлен из обзора пула. ${poolFlipperAddress.type === 'sell' ? 'Продал' : 'Купил'} SPERM`
      });
      if (response.data.success) {
        setShowPoolFlipperDialog(false);
        setPoolFlipperReason('');
        setPoolFlipperAddress(null);
        // Перезагружаем данные пула
        loadPoolOverview(poolDetailed);
      }
    } catch (err) {
      setPoolError(err.response?.data?.error || err.message);
    }
  };

  // Открыть диалог добавления в флипперы из пула
  const openPoolFlipperDialog = (walletData) => {
    setPoolFlipperAddress(walletData);
    setPoolFlipperReason('');
    setShowPoolFlipperDialog(true);
  };

  // Копировать адрес
  const copyAddress = (addr) => {
    navigator.clipboard.writeText(addr);
  };

  // Открыть в TonScan
  const openInTonScan = (addr) => {
    window.open(`https://tonscan.org/address/${addr}`, '_blank');
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // === ОБЗОР ПУЛА ===

  // Загрузка обзора пула
  const loadPoolOverview = async (detailed = false) => {
    setPoolLoading(true);
    setPoolError('');
    setPoolDetailed(detailed);

    try {
      const endpoint = detailed ? '/wallet-scanner/pool-overview-detailed' : '/wallet-scanner/pool-overview';
      // Передаём параметры фильтрации на сервер (фильтруется ДО limit)
      const params = new URLSearchParams({
        limit: poolLimit,
        hideMM: hideMM,
        hideSystem: hideSystem,
        type: poolTypeFilter
      });
      const response = await adminAPI.get(`${endpoint}?${params}`);
      if (response.data.success) {
        setPoolData(response.data.data);
      } else {
        setPoolError(response.data.error || 'Ошибка загрузки');
      }
    } catch (err) {
      setPoolError(err.response?.data?.error || err.message);
    }

    setPoolLoading(false);
  };

  // Загрузка деталей связи
  const loadConnectionDetails = async (wallet) => {
    if (connectionDetails[wallet]) {
      // Уже загружено - просто разворачиваем/сворачиваем
      setExpandedWallet(expandedWallet === wallet ? null : wallet);
      return;
    }

    setLoadingConnection(prev => ({ ...prev, [wallet]: true }));

    try {
      const response = await adminAPI.get(`/wallet-scanner/connection-details/${encodeURIComponent(wallet)}`);
      if (response.data.success) {
        setConnectionDetails(prev => ({ ...prev, [wallet]: response.data.data }));
        setExpandedWallet(wallet);
      }
    } catch (err) {
      console.error('Ошибка загрузки связи:', err);
    }

    setLoadingConnection(prev => ({ ...prev, [wallet]: false }));
  };

  // Скачать отчёт по кошельку
  const downloadReport = async (wallet) => {
    try {
      const response = await adminAPI.get(`/wallet-scanner/wallet-report/${encodeURIComponent(wallet)}`);
      if (response.data.success) {
        const text = response.data.data.text;
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wallet_report_${wallet.slice(-8)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Ошибка скачивания отчёта:', err);
    }
  };

  // Анализировать кошелёк из пула
  const analyzeFromPool = (wallet) => {
    setAddress(wallet);
    setActiveTab(1); // Переключаем на вкладку "Анализ кошелька"
    setTimeout(() => analyzeWallet(), 100);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.4rem', md: '2.125rem' } }}>
        🔍 Сканер кошельков
      </Typography>

      {/* Вкладки */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="📊 Пул SPERM" sx={{ minWidth: 'auto', px: { xs: 1.5, md: 2 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
          <Tab label="🔎 Анализ кошелька" sx={{ minWidth: 'auto', px: { xs: 1.5, md: 2 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
          <Tab label="💰 Profit Scanner" sx={{ minWidth: 'auto', px: { xs: 1.5, md: 2 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
        </Tabs>
      </Paper>

      {/* Вкладка 2: Анализ одного кошелька */}
      {activeTab === 1 && (
        <>
          {/* Форма поиска */}
          <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Адрес кошелька"
                  placeholder="EQ... или 0:... или часть адреса (минимум 6 символов)"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && analyzeWallet()}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
                  onClick={analyzeWallet}
                  disabled={loading || address.length < 6}
                >
                  {loading ? 'Анализ...' : 'Анализировать'}
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </Paper>

          {/* Результат анализа */}
          {result && (
            <Grid container spacing={3}>
              {/* Вердикт */}
              <Grid item xs={12}>
                <Alert
                  severity={result.verdict?.isFlipper ? 'error' : 'success'}
                  icon={result.verdict?.isFlipper ? <Warning /> : <CheckCircle />}
                  action={
                    !result.system?.inWalletRules && (
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => setShowAddDialog(true)}
                      >
                        В флипперы
                      </Button>
                    )
                  }
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {result.verdict?.isFlipper ? '🚨 ФЛИППЕР' : '✅ НЕ флиппер'}
                    {result.verdict?.confidence && ` (уверенность: ${result.verdict.confidence})`}
                  </Typography>
                  <Typography variant="body2">{result.verdict?.reason}</Typography>
                </Alert>
              </Grid>

              {/* Статистика блокчейна */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="h6">📊 Блокчейн</Typography>
                      <IconButton size="small" onClick={() => toggleSection('blockchain')}>
                        {expandedSection.blockchain ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Collapse in={expandedSection.blockchain}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 2, bgcolor: 'success.dark', textAlign: 'center' }}>
                            <TrendingUp sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h5">{formatNumber(result.blockchain?.totalBought)}</Typography>
                            <Typography variant="caption">SPERM куплено</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 2, bgcolor: 'error.dark', textAlign: 'center' }}>
                            <TrendingDown sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h5">{formatNumber(result.blockchain?.totalSold)}</Typography>
                            <Typography variant="caption">SPERM продано</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <AccountBalance sx={{ fontSize: 32, mb: 1, color: 'primary.main' }} />
                            <Typography variant="h5">{formatNumber(result.blockchain?.balance)}</Typography>
                            <Typography variant="caption">Баланс SPERM</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h3" color={result.blockchain?.profitTon >= 0 ? 'success.main' : 'error.main'}>
                              {result.blockchain?.profitTon >= 0 ? '+' : ''}{formatNumber(result.blockchain?.profitTon)} TON
                            </Typography>
                            <Typography variant="caption">Профит</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* TonMoon операции - показываем если есть */}
                      {(result.blockchain?.totalTonmoonDeposits > 0 || result.blockchain?.totalTonmoonWithdrawals > 0) && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            🌙 TonMoon операции
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1.5, bgcolor: 'info.dark', textAlign: 'center' }}>
                                <Typography variant="h6">{formatNumber(result.blockchain?.totalTonmoonDeposits || 0)}</Typography>
                                <Typography variant="caption">Депозиты SPERM</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1.5, bgcolor: 'warning.dark', textAlign: 'center' }}>
                                <Typography variant="h6">{formatNumber(result.blockchain?.totalTonmoonWithdrawals || 0)}</Typography>
                                <Typography variant="caption">Выводы SPERM</Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        </>
                      )}

                      {/* Прочие переводы - показываем если есть */}
                      {(result.blockchain?.totalTransfersIn > 0 || result.blockchain?.totalTransfersOut > 0) && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            📤 Переводы (не DEX, не TonMoon)
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                                <Typography variant="h6" color="success.main">+{formatNumber(result.blockchain?.totalTransfersIn || 0)}</Typography>
                                <Typography variant="caption">Входящие</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                                <Typography variant="h6" color="error.main">-{formatNumber(result.blockchain?.totalTransfersOut || 0)}</Typography>
                                <Typography variant="caption">Исходящие</Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        </>
                      )}

                      <Divider sx={{ my: 2 }} />

                      {/* Покупки */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, cursor: 'pointer' }}
                          onClick={() => toggleSection('buys')}
                        >
                          <Typography variant="subtitle2" color="success.main">
                            Покупки ({result.blockchain?.buys?.length || 0})
                          </Typography>
                          {expandedSection.buys ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                        <Collapse in={expandedSection.buys}>
                          <TableContainer sx={{ maxHeight: 200, overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 350 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Дата</TableCell>
                                  <TableCell align="right">SPERM</TableCell>
                                  <TableCell align="right">TON</TableCell>
                                  <TableCell align="right">Цена</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {result.blockchain?.buys?.map((item, idx) => (
                                  <TableRow key={idx} sx={{ bgcolor: item.amountEstimated ? 'rgba(255,193,7,0.1)' : 'inherit' }}>
                                    <TableCell>{item.date}</TableCell>
                                    <TableCell align="right" sx={{ color: 'success.main' }}>
                                      +{formatNumber(item.amount)}
                                      {item.amountEstimated && <Tooltip title="Оценка по исторической цене"><span style={{ color: '#ffc107' }}> ~</span></Tooltip>}
                                    </TableCell>
                                    <TableCell align="right">{formatNumber(item.ton, 4)}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {item.pricePerSperm ? formatNumber(item.pricePerSperm, 6) : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </Box>

                      {/* Продажи */}
                      <Box>
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, cursor: 'pointer' }}
                          onClick={() => toggleSection('sells')}
                        >
                          <Typography variant="subtitle2" color="error.main">
                            Продажи ({result.blockchain?.sells?.length || 0})
                          </Typography>
                          {expandedSection.sells ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                        <Collapse in={expandedSection.sells}>
                          <TableContainer sx={{ maxHeight: 200, overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 350 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Дата</TableCell>
                                  <TableCell align="right">SPERM</TableCell>
                                  <TableCell align="right">TON</TableCell>
                                  <TableCell align="right">Цена</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {result.blockchain?.sells?.map((item, idx) => (
                                  <TableRow key={idx} sx={{ bgcolor: item.amountEstimated ? 'rgba(255,193,7,0.1)' : 'inherit' }}>
                                    <TableCell>{item.date}</TableCell>
                                    <TableCell align="right" sx={{ color: 'error.main' }}>
                                      -{formatNumber(item.amount)}
                                      {item.amountEstimated && <Tooltip title="Оценка по исторической цене"><span style={{ color: '#ffc107' }}> ~</span></Tooltip>}
                                    </TableCell>
                                    <TableCell align="right">{formatNumber(item.ton, 4)}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {item.pricePerSperm ? formatNumber(item.pricePerSperm, 6) : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>

              {/* Проверка в системе */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="h6">🎮 В нашей системе</Typography>
                      <Chip
                        label={
                          result.system?.found
                            ? (result.system?.inUsers || result.system?.details?.users?.length > 0
                                ? 'Найден (игрок)'
                                : result.system?.inDeposits
                                  ? 'Найден (депозиты)'
                                  : result.system?.inTransactions
                                    ? 'Найден (транзакции)'
                                    : result.system?.inPoolEvents
                                      ? 'Найден (пул)'
                                      : 'Найден')
                            : 'Не найден'
                        }
                        color={result.system?.found ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {/* Users */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.system?.inUsers ? <CheckCircle color="success" fontSize="small" /> : <Error color="disabled" fontSize="small" />}
                        <Typography variant="subtitle2">👤 Игроки ({result.system?.details?.users?.length || 0})</Typography>
                      </Box>
                      {result.system?.details?.users?.map((user, idx) => (
                        <Paper key={idx} sx={{ p: 1.5, mb: 1, bgcolor: 'rgba(99, 102, 241, 0.1)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography
                                variant="body1"
                                component="a"
                                href={`/users?id=${user.id}`}
                                sx={{ color: '#6366f1', textDecoration: 'none', fontWeight: 'bold', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                onClick={(e) => { e.preventDefault(); window.location.href = `/users?id=${user.id}`; }}
                              >
                                @{user.username || `user_${user.telegram_id}`}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                ID: {user.id} | TG: {user.telegram_id}
                              </Typography>
                            </Box>
                            <Tooltip title="Открыть карточку">
                              <IconButton size="small" onClick={() => window.location.href = `/users?id=${user.id}`}>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      ))}
                    </Box>

                    {/* Deposits */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.system?.inDeposits ? <CheckCircle color="success" fontSize="small" /> : <Error color="disabled" fontSize="small" />}
                        <Typography variant="subtitle2">💰 Депозиты ({result.system?.details?.deposits?.length || 0})</Typography>
                      </Box>
                      {result.system?.details?.deposits?.slice(0, 3).map((dep, idx) => (
                        <Paper key={idx} sx={{ p: 1, mb: 0.5, bgcolor: 'rgba(34, 197, 94, 0.1)' }}>
                          <Typography variant="body2">
                            <strong>{formatNumber(dep.amount)} SPERM</strong> | {new Date(dep.created_at).toLocaleString('ru-RU')}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>

                    {/* Withdrawals */}
                    {(result.system?.inWithdrawals || result.system?.details?.withdrawals?.length > 0) && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CheckCircle color="success" fontSize="small" />
                          <Typography variant="subtitle2">📤 Выводы ({result.system?.details?.withdrawals?.length || 0})</Typography>
                        </Box>
                        {result.system?.details?.withdrawals?.slice(0, 3).map((wd, idx) => (
                          <Paper key={idx} sx={{ p: 1, mb: 0.5, bgcolor: 'rgba(251, 146, 60, 0.1)' }}>
                            <Typography variant="body2">
                              <strong>{formatNumber(wd.amount)} SPERM</strong> | {new Date(wd.created_at).toLocaleString('ru-RU')} | {wd.status}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Stakings */}
                    {(result.system?.inStakings || result.system?.details?.stakings?.length > 0) && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CheckCircle color="success" fontSize="small" />
                          <Typography variant="subtitle2">📈 Стейкинги ({result.system?.details?.stakings?.length || 0})</Typography>
                        </Box>
                        {result.system?.details?.stakings?.slice(0, 3).map((st, idx) => (
                          <Paper key={idx} sx={{ p: 1, mb: 0.5, bgcolor: 'rgba(99, 102, 241, 0.1)' }}>
                            <Typography variant="body2">
                              <strong>{formatNumber(st.amount)} SPERM</strong> | {new Date(st.created_at).toLocaleString('ru-RU')}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Transactions */}
                    {(result.system?.inTransactions || result.system?.details?.transactions?.length > 0) && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CheckCircle color="info" fontSize="small" />
                          <Typography variant="subtitle2">📋 Транзакции ({result.system?.details?.transactions?.length || 0})</Typography>
                        </Box>
                        {result.system?.details?.transactions?.slice(0, 3).map((tx, idx) => (
                          <Paper key={idx} sx={{ p: 1, mb: 0.5, bgcolor: 'rgba(59, 130, 246, 0.1)' }}>
                            <Typography variant="body2">
                              <strong>{tx.type}</strong>: {formatNumber(tx.amount)} SPERM | {new Date(tx.created_at).toLocaleString('ru-RU')}
                              {tx.username && <span> | @{tx.username}</span>}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Pool Events */}
                    {(result.system?.inPoolEvents || result.system?.details?.poolEvents?.length > 0) && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CheckCircle color="warning" fontSize="small" />
                          <Typography variant="subtitle2">🔄 События пула ({result.system?.details?.poolEvents?.length || 0})</Typography>
                        </Box>
                        {result.system?.details?.poolEvents?.slice(0, 3).map((ev, idx) => (
                          <Paper key={idx} sx={{ p: 1, mb: 0.5, bgcolor: 'rgba(234, 179, 8, 0.1)' }}>
                            <Typography variant="body2">
                              <strong>{ev.event_type}</strong>: {formatNumber(ev.sperm_amount)} SPERM / {formatNumber(ev.ton_amount, 4)} TON
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Wallet Rules */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.system?.inWalletRules ? <Block color="error" fontSize="small" /> : <Error color="disabled" fontSize="small" />}
                        <Typography variant="subtitle2" color={result.system?.inWalletRules ? 'error' : 'inherit'}>
                          ⚠️ Черный список
                        </Typography>
                      </Box>
                      {result.system?.inWalletRules && (
                        <Alert severity="error" sx={{ py: 0.5 }}>Кошелек в черном списке!</Alert>
                      )}
                    </Box>

                    {/* Действия */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Копировать">
                        <IconButton onClick={() => copyAddress(result.address)}><ContentCopy /></IconButton>
                      </Tooltip>
                      <Tooltip title="TonScan">
                        <IconButton onClick={() => openInTonScan(result.address)}><OpenInNew /></IconButton>
                      </Tooltip>
                      <Tooltip title="Скачать отчёт">
                        <IconButton onClick={() => downloadReport(result.address)}><Download /></IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Секция глубокого анализа связей */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTree color="secondary" />
              Глубокий анализ связей
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Поиск связей этого кошелька с пользователями системы через цепочку транзакций.
              Анализируются SPERM свопы и TON переводы на {deepScanDepth} уровн{deepScanDepth === 1 ? 'е' : deepScanDepth < 5 ? 'ях' : 'ях'}.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: { xs: 'auto', sm: 180 } }}>
                <InputLabel>Глубина поиска</InputLabel>
                <Select
                  value={deepScanDepth}
                  label="Глубина поиска"
                  onChange={(e) => setDeepScanDepth(e.target.value)}
                  disabled={deepScanLoading}
                >
                  <MenuItem value={1}>1 уровень (~30 сек)</MenuItem>
                  <MenuItem value={2}>2 уровня (~3 мин)</MenuItem>
                  <MenuItem value={3}>3 уровня (~10-15 мин)</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="secondary"
                startIcon={deepScanLoading ? <CircularProgress size={20} color="inherit" /> : <AccountTree />}
                onClick={startDeepScan}
                disabled={deepScanLoading || !address}
              >
                {deepScanLoading ? 'Сканирование...' : 'Найти связи'}
              </Button>

              {deepScanLoading && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Stop />}
                  onClick={abortDeepScan}
                >
                  Остановить
                </Button>
              )}
            </Stack>

            {/* Прогресс сканирования */}
            {deepScanLoading && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={deepScanProgress.percent || 0}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Проверено {deepScanProgress.current} из ~{deepScanProgress.total || '?'} кошельков
                  ({(deepScanProgress.percent || 0).toFixed(1)}%)
                </Typography>
              </Box>
            )}

            {/* Ошибка */}
            {deepScanError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDeepScanError('')}>
                {deepScanError}
              </Alert>
            )}

            {/* Результаты глубокого сканирования */}
            {deepScanResult && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={deepScanResult.connections?.length > 0 ? "warning" : "success"}
                  sx={{ mb: 2 }}
                >
                  {deepScanResult.connections?.length > 0
                    ? `🔗 Найдено ${deepScanResult.connections.length} связей с системой!`
                    : '✅ Связей с системой не найдено'
                  }
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Проверено {deepScanResult.stats?.walletsScanned || 0} кошельков за {deepScanResult.duration || '?'}
                  </Typography>
                </Alert>

                {/* Список найденных связей */}
                {deepScanResult.connections?.map((conn, i) => (
                  <Card key={i} sx={{ mt: 2, bgcolor: 'warning.dark', color: 'warning.contrastText' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        🔗 Связь #{i + 1} (глубина: {conn.depth} уровн{conn.depth === 1 ? 'ь' : conn.depth < 5 ? 'я' : 'ей'})
                      </Typography>

                      {/* Информация о найденном кошельке в системе */}
                      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {conn.systemInfo?.inUsers && (
                          <Chip
                            label={`👤 Пользователь: ${conn.systemInfo.username || `ID ${conn.systemInfo.userId}`}`}
                            size="small"
                            color="info"
                          />
                        )}
                        {conn.systemInfo?.inDeposits && (
                          <Chip label="💰 Есть депозиты" size="small" color="success" />
                        )}
                        {conn.systemInfo?.inWithdrawals && (
                          <Chip label="📤 Есть выводы" size="small" color="warning" />
                        )}
                        {conn.systemInfo?.inWallets && (
                          <Chip label="💼 В кошельках" size="small" color="primary" />
                        )}
                        {conn.systemInfo?.inPool && (
                          <Chip label="🏊 В пуле" size="small" color="secondary" />
                        )}
                      </Box>

                      {/* Визуализация пути */}
                      <Stepper orientation="vertical" sx={{ mt: 1 }}>
                        {conn.path?.map((step, j) => (
                          <Step key={j} active completed>
                            <StepLabel
                              StepIconProps={{
                                sx: {
                                  color: step.type === 'target' ? 'info.main' :
                                         step.type === 'found' ? 'success.main' : 'inherit'
                                }
                              }}
                            >
                              <Typography variant="body2">
                                {step.type === 'target' && '🎯 Целевой кошелек'}
                                {step.type === 'ton_transfer' && `💎 TON перевод (${formatNumber(step.amount)} TON) ${step.direction === 'out' ? '→' : '←'}`}
                                {step.type === 'sperm_swap' && `🪙 SPERM своп (${formatNumber(step.amount)} SPERM) ${step.direction === 'out' ? '→' : '←'}`}
                                {step.type === 'jetton_transfer' && `🎫 Jetton перевод ${step.direction === 'out' ? '→' : '←'}`}
                                {step.type === 'found' && '✅ Найден в системе!'}
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.2)', p: 0.5, borderRadius: 1 }}
                                >
                                  {step.wallet ? formatAddress(step.wallet) : '-'}
                                </Typography>
                                {step.wallet && (
                                  <>
                                    <Tooltip title="Копировать">
                                      <IconButton
                                        size="small"
                                        onClick={() => navigator.clipboard.writeText(step.wallet)}
                                      >
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="TonScan">
                                      <IconButton
                                        size="small"
                                        onClick={() => window.open(`https://tonscan.org/address/${step.wallet}`, '_blank')}
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </StepContent>
                          </Step>
                        ))}
                      </Stepper>
                    </CardContent>
                  </Card>
                ))}

                {/* Статистика */}
                {deepScanResult.stats && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      📊 Статистика: проверено {deepScanResult.stats.walletsScanned} кошельков,
                      найдено {deepScanResult.stats.connectionsFound} связей
                      {deepScanResult.stats.deepestConnection > 0 &&
                        `, максимальная глубина: ${deepScanResult.stats.deepestConnection}`
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Вкладка 1: Обзор пула */}
      {activeTab === 0 && (
        <Paper sx={{ p: { xs: 1.5, md: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            📊 Последние операции в пуле SPERM
          </Typography>

          {/* Управление */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <ButtonGroup variant="outlined" size="small">
              {[10, 30, 50, 100].map(n => (
                <Button
                  key={n}
                  variant={poolLimit === n ? 'contained' : 'outlined'}
                  onClick={() => setPoolLimit(n)}
                >
                  {n}
                </Button>
              ))}
            </ButtonGroup>

            {/* Фильтр по типу операций */}
            <ButtonGroup variant="outlined" size="small">
              <Button
                variant={poolTypeFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setPoolTypeFilter('all')}
              >
                Все
              </Button>
              <Button
                variant={poolTypeFilter === 'buy' ? 'contained' : 'outlined'}
                color="success"
                onClick={() => setPoolTypeFilter('buy')}
              >
                BUY
              </Button>
              <Button
                variant={poolTypeFilter === 'sell' ? 'contained' : 'outlined'}
                color="error"
                onClick={() => setPoolTypeFilter('sell')}
              >
                SELL
              </Button>
            </ButtonGroup>

            {/* Чекбоксы скрытия MM и SYSTEM */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideMM}
                  onChange={(e) => setHideMM(e.target.checked)}
                  size="small"
                  color="warning"
                />
              }
              label="Скрыть MM"
              sx={{ mr: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideSystem}
                  onChange={(e) => setHideSystem(e.target.checked)}
                  size="small"
                  color="info"
                />
              }
              label="Скрыть SYSTEM"
            />

            <Button
              variant="contained"
              startIcon={poolLoading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
              onClick={() => loadPoolOverview(false)}
              disabled={poolLoading}
            >
              Быстрое сканирование
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={poolLoading ? <CircularProgress size={18} color="inherit" /> : <Analytics />}
              onClick={() => loadPoolOverview(true)}
              disabled={poolLoading}
            >
              Подробный анализ (~30 сек)
            </Button>
          </Box>

          {poolLoading && <LinearProgress sx={{ mb: 2 }} />}
          {poolError && <Alert severity="error" sx={{ mb: 2 }}>{poolError}</Alert>}

          {/* Статистика */}
          {poolData?.stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center', bgcolor: 'primary.dark' }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{poolData.stats.totalOperations || poolData.stats.uniqueWallets}</Typography>
                  <Typography variant="caption">{poolDetailed ? 'Кошельков' : 'Операций'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center', bgcolor: 'success.dark' }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{poolData.stats.buys || '-'}</Typography>
                  <Typography variant="caption">Покупок</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center', bgcolor: 'error.dark' }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{poolData.stats.sells || '-'}</Typography>
                  <Typography variant="caption">Продаж</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center', bgcolor: poolData.stats.withConnection > 0 ? 'warning.dark' : 'grey.700' }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{poolData.stats.withConnection}</Typography>
                  <Typography variant="caption">Связей с проектом</Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Таблица операций (быстрый режим) */}
          {poolData?.operations && !poolDetailed && (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Кошелёк</TableCell>
                    <TableCell align="center">Тип</TableCell>
                    <TableCell align="right">SPERM</TableCell>
                    <TableCell align="right">TON</TableCell>
                    <TableCell align="center">Игрок</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Фильтрация на клиенте по флагам isSystem/isMarketMaker */}
                  {poolData.operations
                    .filter(op => !hideMM || !op.isMarketMaker)
                    .filter(op => !hideSystem || !op.isSystem)
                    .map((op, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow
                        sx={{
                          bgcolor: op.hasConnection ? 'rgba(255, 193, 7, 0.1)' : 'inherit',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {op.isSystem && (
                              <Chip label="SYSTEM" size="small" color="info" sx={{ fontSize: '10px', height: 20 }} />
                            )}
                            {op.isMarketMaker && (
                              <Chip label="MM" size="small" color="warning" sx={{ fontSize: '10px', height: 20 }} />
                            )}
                            <Tooltip title={op.walletFriendly || op.wallet}>
                              <code style={{ fontSize: '12px' }}>{op.walletShort}</code>
                            </Tooltip>
                            <IconButton size="small" onClick={() => copyAddress(op.walletFriendly || op.wallet)}>
                              <ContentCopy sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={op.type === 'sell' ? 'SELL' : 'BUY'}
                            size="small"
                            color={op.type === 'sell' ? 'error' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ color: op.type === 'sell' ? 'error.main' : 'success.main' }}>
                          {op.type === 'sell' ? '-' : '+'}{formatNumber(op.spermAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {op.type === 'sell' ? '+' : '-'}{formatNumber(op.tonAmount, 4)}
                        </TableCell>
                        <TableCell align="center">
                          {op.hasConnection ? (
                            <Tooltip title={`User ID: ${op.userId}, Telegram: ${op.telegramId || '-'}`}>
                              <Button
                                size="small"
                                variant="text"
                                color="warning"
                                href={`/users/${op.userId}`}
                                target="_blank"
                                startIcon={<Link />}
                                sx={{ textTransform: 'none', fontSize: '12px' }}
                              >
                                @{op.username || `ID:${op.telegramId}`}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Анализировать">
                            <IconButton size="small" onClick={() => analyzeFromPool(op.wallet)}>
                              <Search sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="TonScan">
                            <IconButton size="small" onClick={() => openInTonScan(op.wallet)}>
                              <OpenInNew sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Скачать отчёт">
                            <IconButton size="small" onClick={() => downloadReport(op.wallet)}>
                              <Download sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {!op.isSystem && !op.isMarketMaker && (
                            <Tooltip title="В флипперы">
                              <IconButton size="small" color="error" onClick={() => openPoolFlipperDialog(op)}>
                                <Block sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Раскрывающиеся детали связи */}
                      {expandedWallet === op.wallet && connectionDetails[op.wallet] && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ bgcolor: 'rgba(255, 193, 7, 0.05)', p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>🔗 Детали связи с проектом:</Typography>
                            {connectionDetails[op.wallet].details?.users?.map((u, i) => (
                              <Chip
                                key={i}
                                label={`👤 @${u.username || 'user_' + u.telegram_id}`}
                                size="small"
                                sx={{ mr: 1, mb: 1 }}
                                onClick={() => window.location.href = `/users?id=${u.id}`}
                              />
                            ))}
                            {connectionDetails[op.wallet].details?.deposits?.length > 0 && (
                              <Chip
                                label={`💰 ${connectionDetails[op.wallet].details.deposits.length} депозитов`}
                                size="small"
                                color="success"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                            {connectionDetails[op.wallet].details?.withdrawals?.length > 0 && (
                              <Chip
                                label={`📤 ${connectionDetails[op.wallet].details.withdrawals.length} выводов`}
                                size="small"
                                color="warning"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                            {connectionDetails[op.wallet].details?.stakings?.length > 0 && (
                              <Chip
                                label={`📈 ${connectionDetails[op.wallet].details.stakings.length} стейкингов`}
                                size="small"
                                color="info"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Таблица кошельков (подробный режим) */}
          {poolData?.wallets && poolDetailed && (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Кошелёк</TableCell>
                    <TableCell align="right">Куплено</TableCell>
                    <TableCell align="right">Продано</TableCell>
                    <TableCell align="right">Продано %</TableCell>
                    <TableCell align="right">Профит TON</TableCell>
                    <TableCell align="center">Связь</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poolData.wallets.map((w, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow
                        sx={{
                          bgcolor: w.hasConnection ? 'rgba(255, 193, 7, 0.1)' : 'inherit',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {w.isSystem && (
                              <Chip label="SYSTEM" size="small" color="info" sx={{ fontSize: '10px', height: 20 }} />
                            )}
                            {w.isMarketMaker && (
                              <Chip label="MM" size="small" color="warning" sx={{ fontSize: '10px', height: 20 }} />
                            )}
                            <Tooltip title={w.walletFriendly || w.wallet}>
                              <code style={{ fontSize: '12px' }}>{w.walletShort}</code>
                            </Tooltip>
                            <IconButton size="small" onClick={() => copyAddress(w.walletFriendly || w.wallet)}>
                              <ContentCopy sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          {formatNumber(w.fullTotalBought || w.totalBought)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {formatNumber(w.fullTotalSold || w.totalSold)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${w.soldPercent || 0}%`}
                            size="small"
                            color={parseFloat(w.soldPercent) >= 70 ? 'error' : parseFloat(w.soldPercent) >= 50 ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: (w.fullProfit || 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}
                        >
                          {(w.fullProfit || 0) >= 0 ? '+' : ''}{formatNumber(w.fullProfit || 0, 4)}
                        </TableCell>
                        <TableCell align="center">
                          {w.hasConnection ? (
                            <Tooltip title={w.connectionSummary || 'Связь найдена'}>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => loadConnectionDetails(w.wallet)}
                              >
                                {loadingConnection[w.wallet] ? <CircularProgress size={16} /> : <Link />}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <LinkOff sx={{ color: 'grey.500', fontSize: 20 }} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Анализировать">
                            <IconButton size="small" onClick={() => analyzeFromPool(w.wallet)}>
                              <Search sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Скачать отчёт">
                            <IconButton size="small" onClick={() => downloadReport(w.wallet)}>
                              <Download sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {!w.isSystem && !w.isMarketMaker && (
                            <Tooltip title="В флипперы">
                              <IconButton size="small" color="error" onClick={() => openPoolFlipperDialog(w)}>
                                <Block sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Раскрывающиеся детали */}
                      {expandedWallet === w.wallet && connectionDetails[w.wallet] && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ bgcolor: 'rgba(255, 193, 7, 0.05)', p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>🔗 Детали связи:</Typography>
                            {connectionDetails[w.wallet].details?.users?.map((u, i) => (
                              <Chip
                                key={i}
                                label={`👤 @${u.username || 'user_' + u.telegram_id}`}
                                size="small"
                                sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                                onClick={() => window.location.href = `/users?id=${u.id}`}
                              />
                            ))}
                            {connectionDetails[w.wallet].details?.deposits?.length > 0 && (
                              <Chip
                                label={`💰 ${connectionDetails[w.wallet].details.deposits.length} депозитов`}
                                size="small"
                                color="success"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!poolData && !poolLoading && (
            <Alert severity="info">
              Нажмите "Быстрое сканирование" чтобы загрузить последние операции в пуле SPERM
            </Alert>
          )}
        </Paper>
      )}

      {/* Вкладка 3: Profit Scanner */}
      {activeTab === 2 && (
        <Paper sx={{ p: { xs: 1.5, md: 3 } }}>
          <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: '1.1rem', md: '1.5rem' } }}>💰 Profit Scanner</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Полный анализ профита пользователя: вложения, выводы, стейкинг, блокчейн-свопы
          </Typography>

          {/* Поиск */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Username"
                placeholder="Введите username (без @)"
                value={profitUsername}
                onChange={(e) => setProfitUsername(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && scanProfit()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={profitLoading ? <CircularProgress size={18} color="inherit" /> : <Analytics />}
                onClick={() => scanProfit()}
                disabled={profitLoading}
                sx={{ height: 56 }}
              >
                {profitLoading ? 'Сканирование...' : 'Сканировать профит'}
              </Button>
            </Grid>
          </Grid>

          {profitLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Сканирование может занять 30-60 секунд (анализ блокчейна)...
              </Typography>
            </Box>
          )}

          {profitError && <Alert severity="error" sx={{ mb: 2 }}>{profitError}</Alert>}

          {/* Результат */}
          {profitData && (() => {
            const d = profitData;
            const p = d.profit;
            const u = p.usdt || {};
            const sa = p.spermAssets || {};
            const earn = p.earnings || {};
            const w = d.wallet || {};
            const pr = d.prices || {};
            // Хелпер: SPERM с эквивалентом в TON и $
            const spermWithTon = (sperm) => {
              if (!sperm) return '0 SPERM';
              const ton = sperm * (pr.spermPriceInTon || 0);
              const usd = ton * (pr.tonPriceUsd || 0);
              return `${formatNumber(sperm)} SPERM (~${formatNumber(ton, 2)} TON / $${formatNumber(usd, 0)})`;
            };
            return (
              <Box>
                {/* Профиль */}
                <Card sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>@{d.user.username}</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={sendProfitReport}
                        disabled={reportSending}
                        sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, minWidth: 'auto', px: 1.5 }}
                      >
                        {reportSending ? '...' : 'В Telegram'}
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      ID: {d.user.id} | Регистрация: {new Date(d.user.createdAt).toLocaleDateString('ru-RU')} | Рефералов: {d.referralCount}
                    </Typography>
                  </CardContent>
                </Card>

                {/* ГЛАВНЫЙ ОБЪЕКТИВНЫЙ ВЕРДИКТ USD (из backend) */}
                {d.verdict && (() => {
                  const v = d.verdict;
                  const isProfit = v.status === 'profit';
                  const color = isProfit ? '#4caf50' : '#f44336';
                  const bg = isProfit ? 'rgba(76,175,80,0.18)' : 'rgba(244,67,54,0.18)';
                  const label = isProfit ? '🟢 В ПЛЮСЕ' : '🔴 В МИНУСЕ';
                  const usdRub = pr.usdRub || 80;
                  return (
                    <Card sx={{ mb: 1.5, bgcolor: bg, border: `3px solid ${color}` }}>
                      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                        <Typography sx={{ textAlign: 'center', fontSize: { xs: '1.3rem', md: '1.8rem' }, fontWeight: 900, color, letterSpacing: 1 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ textAlign: 'center', fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, color }}>
                          {isProfit ? '+' : ''}${formatNumber(v.netPnlUsd, 2)}
                        </Typography>
                        <Typography sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', md: '1.1rem' }, color: 'text.secondary', mb: 1 }}>
                          ({isProfit ? '+' : ''}{formatNumber(v.pnlPercent, 1)}% от вложений) • ≈{formatNumber(v.netPnlUsd * usdRub, 0)} ₽
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={1} sx={{ fontSize: '0.85rem' }}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">ВЛОЖИЛ</Typography>
                            <Typography variant="h6" sx={{ color: '#ff9800' }}>${formatNumber(v.invested.total, 0)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>DEX TON: ${formatNumber(v.invested.dexTonUsd, 0)}</Typography>
                            {v.invested.directSwapUsd > 0 && <Typography variant="caption" sx={{ display: 'block' }}>DirectSwap: ${formatNumber(v.invested.directSwapUsd, 0)}</Typography>}
                            {v.invested.usdtDeposited > 0 && <Typography variant="caption" sx={{ display: 'block' }}>USDT: ${formatNumber(v.invested.usdtDeposited, 0)}</Typography>}
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">ВЫВЕЛ (кэш)</Typography>
                            <Typography variant="h6" sx={{ color: '#03a9f4' }}>${formatNumber(v.cashedOut.total, 0)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>DEX TON: ${formatNumber(v.cashedOut.dexTonUsd, 0)}</Typography>
                            {v.cashedOut.usdtWithdrawn > 0 && <Typography variant="caption" sx={{ display: 'block' }}>USDT: ${formatNumber(v.cashedOut.usdtWithdrawn, 0)}</Typography>}
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">ИМЕЕТ В ПРОЕКТЕ</Typography>
                            <Typography variant="h6" sx={{ color }}>${formatNumber(v.currentAssets.total, 0)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>SPERM-кош: ${formatNumber(v.currentAssets.spermOnWalletUsd, 0)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>SPERM-игра: ${formatNumber(v.currentAssets.spermInGameUsd, 0)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>USDT-игра: ${formatNumber(v.currentAssets.usdtInGameUsd, 0)}</Typography>
                            {v.personalCash && v.personalCash.total > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#999', fontStyle: 'italic' }}>
                                ~ личный кэш (не в P&L): ${formatNumber(v.personalCash.total, 0)} (TON ${formatNumber(v.personalCash.ton, 2)} + USDT ${formatNumber(v.personalCash.usdt, 2)})
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                          Формула: (ВЫВЕЛ + ИМЕЕТ) − ВЛОЖИЛ. Цены TON/USD — исторические
                          на момент транзакций, SPERM — текущая DEX-цена.
                        </Typography>
                        {((w.usdtBlockchainIn || 0) > 0 || (w.usdtBlockchainOut || 0) > 0) && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#9c27b0' }}>
                            📊 USDT on-chain: входящие ${formatNumber(w.usdtBlockchainIn, 2)} / исходящие ${formatNumber(w.usdtBlockchainOut, 2)} (по всем кошелькам)
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* ВЕРДИКТ — в плюсе или минусе */}
                {(() => {
                  const tonInvested = p.totalTonIn || 0;
                  // Зафиксировал = обменял SPERM → TON на DEX
                  const tonFromSells = p.totalTonOut || 0;
                  // SPERM на кошельке = только сейчас лежит (НЕ плюсуем вывод из игры)
                  const spermOnWalletNow = sa.onWallet || w.spermOnWallet || 0;
                  const spermOnWalletTon = spermOnWalletNow * (pr.spermPriceInTon || 0);
                  // USDT
                  const usdtIn = u.deposited || 0;
                  const usdtOut = (u.withdrawn || 0) + (u.balance || 0);
                  const usdtPnl = usdtOut - usdtIn;
                  const tonUsd = pr.tonPriceUsd || 0;

                  // Долларовый P&L: $ вложил тогда vs $ имеет на руках сейчас
                  const mb = d.blockchain.monthlyBuys || {};
                  const usdInvested = Object.values(mb).reduce((s, m) => s + (m.usdSpent || m.ton * tonUsd), 0) + usdtIn;
                  const usdFromSells = (d.blockchain.dexSells || []).reduce((s, sl) => s + (sl.usdReceived || sl.ton * tonUsd), 0);
                  const usdSpermOnWallet = spermOnWalletNow * (pr.spermPriceUsd || 0);
                  const usdTonOnWallet = (w.tonOnWallet || p.tonOnWallet || 0) * tonUsd;
                  const usdNow = usdFromSells + usdSpermOnWallet + usdTonOnWallet + usdtOut;
                  const usdPnl = usdNow - usdInvested;

                  // Рублёвый P&L (курс ЦБ РФ)
                  const usdRub = pr.usdRub || 80;
                  const rubInvested = usdInvested * usdRub;
                  const rubNow = usdNow * usdRub;
                  const rubPnl = rubNow - rubInvested;

                  return (
                    <>
                    <Card sx={{ mb: 1.5, bgcolor: tonFromSells > 0 ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.05)', border: `2px solid ${tonFromSells > 0 ? '#4caf50' : '#666'}` }}>
                      <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                        <Grid container spacing={1}>
                          <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>Вложил TON</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: '#f44336', fontSize: { xs: '1.1rem', md: '1.8rem' } }}>
                              {formatNumber(tonInvested, 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}>
                              обменял в TON: <b style={{color: '#4caf50'}}>{formatNumber(tonFromSells, 0)}</b>
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>SPERM на кошельке</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: '#2196f3', fontSize: { xs: '1.1rem', md: '1.8rem' } }}>
                              {formatNumber(spermOnWalletNow, 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}>~{formatNumber(spermOnWalletTon, 0)} TON / ${formatNumber(spermOnWalletNow * (pr.spermPriceUsd || 0), 0)}</Typography>
                          </Grid>
                          <Grid item xs={4} sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>USDT</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: usdtPnl >= 0 ? '#4caf50' : '#f44336', fontSize: { xs: '1.1rem', md: '1.8rem' } }}>
                              {usdtPnl >= 0 ? '+' : ''}{formatNumber(usdtPnl, 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}>вложил {formatNumber(usdtIn, 0)} → имеет {formatNumber(usdtOut, 0)}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    {/* Долларовый P&L с SPERM */}
                    <Card sx={{ mb: 1, bgcolor: usdPnl >= 0 ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)', border: `1px solid ${usdPnl >= 0 ? '#4caf50' : '#f44336'}` }}>
                      <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } }, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>P&L в $ (вложил тогда → имеет сейчас)</Typography>
                        <Typography sx={{ fontWeight: 'bold', color: usdPnl >= 0 ? '#4caf50' : '#f44336', fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
                          {usdPnl >= 0 ? '+' : ''}${formatNumber(usdPnl, 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                          ~{rubPnl >= 0 ? '+' : ''}{formatNumber(rubPnl, 0)} ₽
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.65rem', md: '0.8rem' } }}>
                          Вложил ${formatNumber(usdInvested, 0)} (~{formatNumber(rubInvested, 0)} ₽) → На руках ${formatNumber(usdNow, 0)} (~{formatNumber(rubNow, 0)} ₽)
                        </Typography>
                      </CardContent>
                    </Card>
                    {/* P&L без SPERM — только TON и USDT */}
                    {(() => {
                      const usdNowNoSperm = usdNow - usdSpermOnWallet;
                      const usdPnlNoSperm = usdNowNoSperm - usdInvested;
                      const rubPnlNoSperm = usdPnlNoSperm * usdRub;
                      return (
                        <Card sx={{ mb: 1.5, bgcolor: usdPnlNoSperm >= 0 ? 'rgba(76,175,80,0.05)' : 'rgba(244,67,54,0.05)', border: `1px dashed ${usdPnlNoSperm >= 0 ? '#4caf50' : '#f44336'}` }}>
                          <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } }, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>P&L без SPERM (только TON + USDT)</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: usdPnlNoSperm >= 0 ? '#4caf50' : '#f44336', fontSize: { xs: '1.2rem', md: '2rem' } }}>
                              {usdPnlNoSperm >= 0 ? '+' : ''}${formatNumber(usdPnlNoSperm, 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.8rem' } }}>
                              ~{rubPnlNoSperm >= 0 ? '+' : ''}{formatNumber(rubPnlNoSperm, 0)} ₽ | На руках без SPERM: ${formatNumber(usdNowNoSperm, 0)}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })()}
                    </>
                  );
                })()}

                {/* Курс */}
                <Alert severity="info" sx={{ mb: 2 }}>
                  Курс: 1 SPERM = {formatNumber(pr.spermPriceInTon, 6)} TON (${formatNumber(pr.spermPriceUsd, 4)}) | 1 TON = ${formatNumber(pr.tonPriceUsd, 2)} | $1 = {formatNumber(pr.usdRub, 2)}₽
                </Alert>

                {/* ВЛОЖИЛ */}
                <Card sx={{ mb: 1.5, border: '1px solid #f44336', '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, color: 'error.main', fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>Вложил</Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}><b>{formatNumber(p.totalTonIn, 2)} TON</b> (${formatNumber((p.totalTonIn || 0) * (pr.tonPriceUsd || 0), 0)}) — купил {formatNumber(p.totalSpermBought)} SPERM на DEX</Typography>
                    {p.avgBuyPrice > 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Ср. цена покупки: {formatNumber(p.avgBuyPrice, 6)} TON/SPERM</Typography>}
                    {d.blockchain.monthlyBuys && Object.keys(d.blockchain.monthlyBuys).length > 0 && (
                      <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid rgba(244,67,54,0.3)' }}>
                        {Object.entries(d.blockchain.monthlyBuys).sort(([a],[b]) => a.localeCompare(b)).map(([month, data]) => (
                          <Typography key={month} variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                            {month}: <b>{formatNumber(data.ton, 0)} TON</b> → {formatNumber(data.sperm, 0)} SPERM ({data.count})
                            {data.tonPriceUsd > 0 && <span style={{color: '#999'}}> | TON=${formatNumber(data.tonPriceUsd, 2)}</span>}
                            {data.usdRub > 0 && <span style={{color: '#999'}}> | $1={formatNumber(data.usdRub, 0)}₽</span>}
                            {data.usdSpent > 0 && <span style={{color: '#ff9800'}}> | <b>${formatNumber(data.usdSpent, 0)}</b></span>}
                            {data.rubSpent > 0 && <span style={{color: '#ff9800'}}> ({formatNumber(data.rubSpent, 0)}₽)</span>}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(u.deposited || 0) > 0 && <Typography variant="body1" sx={{ mt: 1 }}><b>{formatNumber(u.deposited)} USDT</b></Typography>}
                  </CardContent>
                </Card>

                {/* НА КОШЕЛЬКЕ (блокчейн, суммарно по всем кошелькам) */}
                <Card sx={{ mb: 1.5, border: '1px solid #4caf50', '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, color: 'success.main', fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      На кошельке (блокчейн) — {(w.walletBalances || []).length || 1} адрес(ов)
                    </Typography>
                    <Typography variant="body1"><b>{formatNumber(w.tonOnWallet || 0, 2)} TON</b> (${formatNumber((w.tonOnWallet || 0) * (pr.tonPriceUsd || 0), 0)})</Typography>
                    <Typography variant="body1">{spermWithTon(w.spermOnWallet || 0)}</Typography>
                    <Typography variant="body1"><b>{formatNumber(w.usdtOnWallet || 0, 2)} USDT</b> (блокчейн)</Typography>
                    {(u.balance || 0) > 0 && <Typography variant="body2" color="text.secondary">+ {formatNumber(u.balance)} USDT (в игре)</Typography>}

                    {(w.walletBalances || []).length > 0 && (
                      <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid rgba(76,175,80,0.3)' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Детализация по кошелькам:
                        </Typography>
                        {w.walletBalances.map((wb, i) => (
                          <Box key={i} sx={{ mb: 0.75 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>
                              {wb.isMain && <span style={{ color: '#4caf50' }}>★ </span>}
                              {wb.wallet}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                              <b>{formatNumber(wb.ton, 2)}</b> TON • <b>{formatNumber(wb.sperm, 0)}</b> SPERM • <b>{formatNumber(wb.usdt, 2)}</b> USDT
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* ОБМЕНЯЛ SPERM → TON (продажи на DEX) */}
                {(p.totalTonOut || 0) > 0 && (
                  <Card sx={{ mb: 2, border: '1px solid #ff9800', '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                    <CardContent>
                      <Typography sx={{ mb: 0.5, color: 'warning.main', fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>Обменял SPERM → TON</Typography>
                      <Typography variant="body1">Продал <b>{formatNumber(p.totalSpermSold)} SPERM</b> → получил <b>{formatNumber(p.totalTonOut, 2)} TON</b> (${formatNumber((p.totalTonOut || 0) * (pr.tonPriceUsd || 0), 0)})</Typography>
                      {p.avgSellPrice > 0 && <Typography variant="body2" color="text.secondary">Ср. цена продажи: {formatNumber(p.avgSellPrice, 6)} TON/SPERM</Typography>}
                      {d.blockchain.dexSells && d.blockchain.dexSells.length > 0 && (
                        <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid rgba(255,152,0,0.3)' }}>
                          {d.blockchain.dexSells.map((s, i) => (
                            <Typography key={i} variant="body2">
                              {s.date}: {formatNumber(s.sperm, 0)} SPERM → <b>{formatNumber(s.ton, 2)} TON</b> (по {formatNumber(s.price, 6)} TON/SPERM)
                              {s.tonPriceUsd > 0 && <span style={{color: '#ff9800'}}> | <b>${formatNumber(s.usdReceived || s.ton * s.tonPriceUsd, 0)}</b> (TON=${formatNumber(s.tonPriceUsd, 2)})</span>}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* У НАС В ИГРЕ */}
                <Card sx={{ mb: 1.5, border: '1px solid #2196f3', '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, color: 'info.main', fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>У нас в игре</Typography>
                    <Typography variant="body1">Баланс: <b>{formatNumber(sa.balance || d.user.balance)} SPERM</b></Typography>
                    <Typography variant="body1">Копилка: <b>{formatNumber(sa.piggybank || d.user.piggybank || 0)} SPERM</b></Typography>
                    <Typography variant="body1">Стейки: <b>{formatNumber(sa.staked || 0)} SPERM</b></Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6">Итого: {spermWithTon(sa.inGame || (sa.balance||0)+(sa.piggybank||0)+(sa.staked||0))}</Typography>
                    {(u.staked || 0) > 0 && <Typography variant="body1" sx={{ mt: 0.5 }}>{formatNumber(u.staked)} USDT (в стейке)</Typography>}
                  </CardContent>
                </Card>

                {/* ИТОГ */}
                <Card sx={{ mb: 1.5, bgcolor: 'rgba(255,255,255,0.08)', border: '2px solid #fff', '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>Итог</Typography>
                    <Typography variant="body1">Вложил: <b>{formatNumber(p.totalTonIn, 2)} TON</b>{(u.deposited||0) > 0 ? ` + ${formatNumber(u.deposited)} USDT` : ''}</Typography>
                    <Typography variant="body1">На кошельке: <b>{formatNumber(w.tonOnWallet || 0, 2)} TON</b> + <b>{formatNumber(w.spermOnWallet || 0)} SPERM</b>{(w.usdtOnWallet||0) > 0 ? ` + ${formatNumber(w.usdtOnWallet, 2)} USDT` : ''}{(u.balance||0) > 0 ? ` + ${formatNumber(u.balance)} USDT (в игре)` : ''}</Typography>
                    {(p.totalTonOut || 0) > 0 && <Typography variant="body1">Обменял в TON: <b>{formatNumber(p.totalSpermSold)} SPERM → {formatNumber(p.totalTonOut, 2)} TON</b></Typography>}
                    <Typography variant="body1">У нас в игре: <b>{formatNumber(sa.inGame || 0)} SPERM</b> (~{formatNumber((sa.inGame||0)*(pr.spermPriceInTon||0), 0)} TON){(u.staked||0) > 0 ? ` + ${formatNumber(u.staked)} USDT` : ''}</Typography>
                    {(u.withdrawn||0) > 0 && <Typography variant="body1">Вывел USDT: <b>{formatNumber(u.withdrawn)} USDT</b>{u.pending > 0 ? ` (+${formatNumber(u.pending)} pending)` : ''}</Typography>}
                  </CardContent>
                </Card>

                {/* Откуда доход */}
                <Card sx={{ mb: 1.5, '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>Откуда доход</Typography>
                    <Grid container spacing={1}>
                      {earn.stakingClaimed > 0 && <Grid item xs={6} sm={4}><Typography variant="body2">Стейкинг: <b>{formatNumber(earn.stakingClaimed)} SPERM</b></Typography></Grid>}
                      {earn.referralDaily > 0 && <Grid item xs={6} sm={4}><Typography variant="body2">Рефералы: <b>{formatNumber((earn.referralDaily||0)+(earn.referralStaking||0)+(earn.referralBonus||0))} SPERM</b></Typography></Grid>}
                      {earn.marathonBurn > 0 && <Grid item xs={6} sm={4}><Typography variant="body2">Марафон: <b>{formatNumber(earn.marathonBurn)} SPERM</b></Typography></Grid>}
                      {earn.starStaking > 0 && <Grid item xs={6} sm={4}><Typography variant="body2">Star стейкинг: <b>{formatNumber(earn.starStaking)} SPERM</b></Typography></Grid>}
                      {earn.taskRewards > 0 && <Grid item xs={6} sm={4}><Typography variant="body2">Задания: <b>{formatNumber(earn.taskRewards)} SPERM</b></Typography></Grid>}
                      <Grid item xs={6} sm={4}><Typography variant="body2" sx={{ color: (earn.slots||0) >= 0 ? 'success.main' : 'error.main' }}>Слоты: <b>{formatNumber(earn.slots || 0)} SPERM</b></Typography></Grid>
                      {earn.stakingEarnedUsdt > 0 && <Grid item xs={6} sm={4}><Typography variant="body2">USDT стейкинг: <b>{formatNumber(earn.stakingEarnedUsdt)} USDT</b></Typography></Grid>}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Кошельки */}
                <Card sx={{ mb: 1.5, '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>Кошельки ({d.blockchain.walletsScanned})</Typography>
                    {d.blockchain.walletDetails.filter(w => !w.error).map((wd, i) => (
                      <Box key={i} sx={{ mb: 1, p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <Tooltip title={wd.wallet}><code style={{ fontSize: 11 }}>{formatAddress(wd.wallet)}</code></Tooltip>
                          <IconButton size="small" onClick={() => copyAddress(wd.wallet)}><ContentCopy sx={{ fontSize: 12 }} /></IconButton>
                          {wd.wallet === d.user.walletAddress && <Chip label="основной" size="small" sx={{ ml: 1 }} />}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {wd.buysCount > 0 && `Купил: ${formatNumber(wd.totalBought)} SPERM за ${formatNumber(wd.totalTonSpent, 2)} TON`}
                          {wd.buysCount > 0 && wd.sellsCount > 0 && ' | '}
                          {wd.sellsCount > 0 && `Продал: ${formatNumber(wd.totalSold)} SPERM за ${formatNumber(wd.totalTonReceived, 2)} TON`}
                          {wd.tonmoonDeposits > 0 && ` | Депозит в игру: ${formatNumber(wd.tonmoonDeposits)} SPERM`}
                          {wd.tonmoonWithdrawals > 0 && ` | Вывод из игры: ${formatNumber(wd.tonmoonWithdrawals)} SPERM`}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>

                {/* Депозиты и выводы */}
                <Card sx={{ mb: 1.5, '& .MuiCardContent-root': { p: { xs: 1.5, md: 2 } } }}>
                  <CardContent>
                    <Typography sx={{ mb: 0.5, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>Депозиты и выводы</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}><Typography variant="body2">Депозиты SPERM: <b>{formatNumber(d.deposits.byCurrency?.SPERM?.total || 0)}</b> ({d.deposits.byCurrency?.SPERM?.count || 0})</Typography></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="body2">Депозиты USDT: <b>{formatNumber(d.deposits.byCurrency?.USDT?.total || 0)}</b> ({d.deposits.byCurrency?.USDT?.count || 0})</Typography></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="body2">Выводы SPERM: <b>{formatNumber(d.withdrawals.SPERM?.approved || 0)}</b> ({d.withdrawals.SPERM?.count || 0})</Typography></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="body2">Выводы USDT: <b>{formatNumber(d.withdrawals.USDT?.approved || 0)}</b> ({d.withdrawals.USDT?.count || 0})</Typography></Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            );
          })()}
        </Paper>
      )}

      {/* Диалог добавления в флипперы (из анализа одного кошелька) */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)}>
        <DialogTitle>Добавить в флипперы</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Адрес: <code>{formatAddress(result?.address)}</code>
          </Typography>
          <TextField
            fullWidth
            label="Причина (опционально)"
            value={addFlipperReason}
            onChange={(e) => setAddFlipperReason(e.target.value)}
            placeholder={`Продал ${result?.blockchain?.soldPercent}% токенов`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Отмена</Button>
          <Button variant="contained" color="error" onClick={addToFlippers}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления в флипперы (из обзора пула) */}
      <Dialog open={showPoolFlipperDialog} onClose={() => setShowPoolFlipperDialog(false)}>
        <DialogTitle>Добавить в флипперы</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Адрес: <code>{poolFlipperAddress?.walletShort || formatAddress(poolFlipperAddress?.wallet)}</code>
          </Typography>
          {poolFlipperAddress?.connectionSummary && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ У кошелька есть связь с системой: {poolFlipperAddress.connectionSummary}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Причина (опционально)"
            value={poolFlipperReason}
            onChange={(e) => setPoolFlipperReason(e.target.value)}
            placeholder={`${poolFlipperAddress?.type === 'sell' ? 'Продал' : 'Купил'} ${formatNumber(poolFlipperAddress?.spermAmount)} SPERM`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPoolFlipperDialog(false)}>Отмена</Button>
          <Button variant="contained" color="error" onClick={addPoolWalletToFlippers}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletScanner;
